import { Auth, Env, Mfe } from '@loca/mfe-vue3'
import { MenuItem } from '@/models/Menu.model'

interface ClickHistoryItem {
  link: string
}

interface MenuClickHistoryCache {
  client: string
  env: string
  userId: string

  // 点击历史
  clickList: ClickHistoryItem[] | null
}

let clickList: ClickHistoryItem[] | null = null
const clickListValidMax = 12 // 最大几条有效数据

// 深度优先遍历树状结构，并针对每个节点执行指定的func函数。
// func 接受2个参数，当前节点和父节点
export function treeForeach(
  tree: any,
  parentNode?: any,
  func?: (c: any, p?: any) => any,
  childrenKey?: string
) {
  if (!(Array.isArray(tree) && typeof func === 'function')) {
    return
  }
  childrenKey = childrenKey || 'children'
  for (let i = 0, il = tree.length; i < il; i++) {
    func(tree[i], parentNode)
    // 遍历子树
    if (tree[i][childrenKey]) {
      treeForeach(tree[i][childrenKey], tree[i], func, childrenKey)
    }
  }
}

// 缓存的 key 区分不同环境
function realCacheKey() {
  const innerCacheKey = '__menu-click-shitory-key__'
  return innerCacheKey
}

// 存入缓存
function _setClickHistoryCache(list: ClickHistoryItem[]) {
  const env = Env.getEnv()
  const { client } = Mfe.getCommonParams()
  const { userId = '' } = Auth.getUserInfo() || {}
  const data: MenuClickHistoryCache = { client, env, userId, clickList: list }

  let localStorageError = false
  clickList = list
  const cacheKey = realCacheKey()
  try {
    localStorage.setItem(cacheKey, JSON.stringify(data))
  } catch {
    localStorageError = true
  }
  if (localStorageError) {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(data))
    } catch {
      //
    }
  }
}

// 获取最近使用的缓存
function _getClickHistoryCache() {
  const cacheKey = realCacheKey()
  let cache: any
  let localStorageError = false
  try {
    // 先尝试从localstorage获取
    cache = localStorage.getItem(cacheKey)
  } catch (error) {
    localStorageError = true
  }
  if (localStorageError) {
    try {
      // 再尝试从sessionstorage获取
      cache = sessionStorage.getItem(cacheKey)
    } catch (error) {
      //
    }
  }

  if (cache) {
    try {
      cache = JSON.parse(cache)
    } catch {
      cache = {}
    }
  }

  cache = cache || {}
  // 仅返回与当前登录用户且环境匹配的缓存数据
  const env = Env.getEnv()
  const { client } = Mfe.getCommonParams()
  const { userId = '' } = Auth.getUserInfo() || {}

  if (cache.userId !== userId || cache.env !== env || cache.client !== client) {
    cache = {}
  }
  clickList = cache.clickList || []
  return cache
}

// 单纯的获取菜单数据
function getClickHistory() {
  // 优先读取缓存
  if (!(Array.isArray(clickList) && clickList.length)) {
    _getClickHistoryCache()
  }

  return clickList || []
}

// 用缓存的【最近使用】数据对菜单列表进行过滤，产生【最近使用】的菜单列表。
// 并根据使用menuList来剔除缓存里的脏数据，并更新缓存
function checkAndUpdateClickHistory(
  menuList: MenuItem[],
  options?: {
    clickArr?: ClickHistoryItem[]
    needUpdateStore?: boolean // 是否需要更新缓存的【最近使用】
  }
) {
  options = options || {}
  const menuListMap: Record<string, MenuItem> = {}
  const callback = (curNode: MenuItem, parenNode?: MenuItem) => {
    if (curNode && curNode.link) {
      menuListMap[curNode.link] = curNode
    }
  }
  // 把tree转为Map
  treeForeach(menuList, null, callback, 'children')
  const hisotryMenuList: any[] = [] // 【最近使用】的菜单列表
  const newClickArr: any[] = [] // 【最近使用】的url
  const newClickArrMap: Record<string, ClickHistoryItem> = {}
  const clickArr = options.clickArr || getClickHistory()
  // 3、假设有click里面10000条数据，但只有3条是真正有效的。则也需要删除其余的数据，并重新更新缓存
  for (let i = 0, il = clickArr.length; i < il; i++) {
    if (clickListValidMax <= 0 || newClickArr.length >= clickListValidMax) {
      // 小于等于0表示禁止
      // 缓存数量不能超过最大数。
      break
    }
    if (
      clickArr[i] &&
      clickArr[i].link &&
      // 过滤掉后端menuList没有的。
      menuListMap[clickArr[i].link] &&
      menuListMap[clickArr[i].link].link &&
      // 去重
      !newClickArrMap[clickArr[i].link]
    ) {
      // 赋值到map，为下一次去重做准备。
      newClickArrMap[clickArr[i].link] = clickArr[i]
      // 过滤掉后端menuList没有的。
      newClickArr.push(clickArr[i])
      hisotryMenuList.push(menuListMap[clickArr[i].link])
    }
  }
  if (options.needUpdateStore) {
    // 用去重，且过滤后的值更新 本地存储的clickList
    // clickList = newClickArr;
    _setClickHistoryCache(newClickArr)
  }
  return {
    historyClickList: newClickArr, // 缓存的【最近使用】的url
    historyMenuList: hisotryMenuList // 【最近使用】的菜单列表
  }
}

// 假设数据里面已经有重复的，我要如何去修复。只有在触发操作的时候才能修复，对吧，什么都不做是不会修复的
// 所以应该是在get或者add的时候进行修复。。但修复也是修复的我们的click list，和原始menu list无关。
// 去重，并更新缓存，需要保证顺序
export function addClickHistory(itemList: string[]) {
  if (!(Array.isArray(clickList) && clickList.length)) {
    _getClickHistoryCache()
  }
  clickList = clickList || []
  if (!(Array.isArray(itemList) && itemList.length)) {
    return
  }
  const clickListCopty: any = clickList.concat()
  let tmpClick
  // 先存储，且要保证顺序
  for (let i = 0, il = itemList.length; i < il; i++) {
    if (itemList[i]) {
      // 即使重复，也要强制覆盖更新
      tmpClick = { link: itemList[i] }
      // 新添加的放在最前边
      clickListCopty.unshift(tmpClick)
    }
  }
  const clickListMap: Record<string, ClickHistoryItem> = {}
  const newClickList: any[] = []
  // 再去重
  for (let h = 0, hl = clickListCopty.length; h < hl; h++) {
    if (clickListValidMax <= 0 || newClickList.length >= clickListValidMax) {
      break
    }
    // 去重
    if (clickListCopty[h] && clickListCopty[h].link && !clickListMap[clickListCopty[h].link]) {
      clickListMap[clickListCopty[h].link] = clickListCopty[h]
      newClickList.push(clickListCopty[h])
    }
  }
  // clickList = newClickList
  _setClickHistoryCache(newClickList)
}

export function filterMenuListByClickHistory(menuList: MenuItem[]) {
  if (!(Array.isArray(menuList) && menuList.length)) {
    return []
  }
  // 2、剔除无效的数据，并自动更新缓存。
  const historData = checkAndUpdateClickHistory(menuList, { needUpdateStore: true })
  return historData.historyMenuList
}
